import { DangerAlert } from "components/common/Alerts";
import { Breadcrumb, BreadcrumbLink } from "components/common/Breadcrumb";
import "components/common/Common.css";
import ContactModel from "components/common/ContactModel";
import ContentLoading from "components/common/ContentLoading";
import serialize from "form-serialize";
import Customer from "interfaces/Customer";
import CustomerContact from "interfaces/CustomerContact";
import UserIdAndName from "interfaces/UserIdAndName";
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import MaskedInput from "react-text-mask";
import apifetch from "utils/apifetch";
import extractErrorMessage from "utils/extractErrorMessage";
import normalizePhone from "utils/normalizePhone";

interface Props {
    id: number;
}

interface State {
    isLoading: boolean;
    data: Customer;
    salespeople: UserIdAndName[];
    hasValidationErrors: boolean;
    errorMessage?: string;
    contactPersonCount: number;
}

export default class Edit extends React.Component<RouteComponentProps<Props>, State> {
    constructor(props: RouteComponentProps<Props>) {
        super(props);

        this.state = {
            isLoading: true,
            data: {
                id: 0,
                name: "",
                city: "",
                zip: "",
                customerContacts: [],
            },
            salespeople: [],
            hasValidationErrors: false,
            errorMessage: undefined,
            contactPersonCount: 0,
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleAddContactClicked = this.handleAddContactClicked.bind(this);
        this.handleRemoveContactClicked = this.handleRemoveContactClicked.bind(this);

        this.handleOnHonorificSelected = this.handleOnHonorificSelected.bind(this);
        this.handleOnNameChanged = this.handleOnNameChanged.bind(this);
        this.handleOnPhoneChanged = this.handleOnPhoneChanged.bind(this);
        this.handleOnTitleChanged = this.handleOnTitleChanged.bind(this);
        this.handleOnEmailChanged = this.handleOnEmailChanged.bind(this);
    }

    public render() {
        if (this.state.isLoading) {
            return (
                <div className="container">
                    <ContentLoading />
                </div>
            );
        }

        return <div className="container">

            <div className="animated fadeIn">
                <Breadcrumb>
                    <BreadcrumbLink to="/">Home</BreadcrumbLink>
                    <BreadcrumbLink to="/customers-bqs">Customers</BreadcrumbLink>
                    <BreadcrumbLink isActive={true}>Edit {this.state.data.name}</BreadcrumbLink>
                </Breadcrumb>
                <div className="card card--form">
                    <div className="card-block">
                        <form name="form" className="form" onSubmit={this.handleSubmit}>
                            <div className="form-wrapper">
                                {this.state.hasValidationErrors && <DangerAlert name="dangerAlert">{this.state.errorMessage || "An error occurred."}</DangerAlert>}
                                <div className="row">
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">Customer ID</span>
                                        <span className="field-value">{this.state.data.id}</span>
                                    </div>
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">Mincron ID</span>
                                        <input type="text" name="mincronId" className="form-control" defaultValue={this.state.data.mincronId ? this.state.data.mincronId.toString() : ""} />
                                    </div>
                                </div>
                                <div className="field-group">
                                    <span className="field-label">Name</span>
                                    <input type="text" name="name" className="form-control" required maxLength={50} onInput={e => e.currentTarget.setCustomValidity("")} onInvalid={e => e.currentTarget.setCustomValidity("Customer name is required.")} defaultValue={this.state.data.name} />
                                </div>
                                <div className="row">
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">Address</span>
                                        <input type="text" name="address" className="form-control" maxLength={50} defaultValue={this.state.data.address} />
                                    </div>
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">City</span>
                                        <input type="text" name="city" className="form-control" autoComplete="new-password" required maxLength={50} onInput={e => e.currentTarget.setCustomValidity("")} onInvalid={e => e.currentTarget.setCustomValidity("City is required.")} defaultValue={this.state.data.city} />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">Address 2</span>
                                        <input type="text" name="address2" className="form-control" maxLength={50} defaultValue={this.state.data.address2} />
                                    </div>
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">State</span>
                                        <input type="text" name="state" className="form-control" autoComplete="new-password" required pattern="[A-Za-z]{2}" maxLength={2} onInput={e => e.currentTarget.setCustomValidity("")} onInvalid={e => e.currentTarget.setCustomValidity("State is required and must be a 2 character abbreviation.")} defaultValue={this.state.data.state} />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">Address 3</span>
                                        <input type="text" name="address3" className="form-control" maxLength={50} defaultValue={this.state.data.address3} />
                                    </div>
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">Zip</span>
                                        <input type="text" name="zip" className="form-control" required pattern="[0-9]{5}" maxLength={5} defaultValue={this.state.data.zip} />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">Phone</span>
                                        <MaskedInput mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]} name="phone" className="form-control" placeholder="(xxx) xxx-xxxx" pattern="\(\d{3}\) \d{3}-\d{4}" onInput={e => e.currentTarget.setCustomValidity("")} onInvalid={e => e.currentTarget.setCustomValidity("Phone number must be in the format of (xxx) xxx-xxxx.")} value={this.state.data.phone} />
                                    </div>
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">Fax</span>
                                        <MaskedInput mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]} name="fax" className="form-control" placeholder="(xxx) xxx-xxxx" pattern="\(\d{3}\) \d{3}-\d{4}" onInput={e => e.currentTarget.setCustomValidity("")} onInvalid={e => e.currentTarget.setCustomValidity("Fax number must be in the format of (xxx) xxx-xxxx.")} value={this.state.data.fax} />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">Salesperson</span>
                                        <select name="salespersonUserId" className="form-control selectbox" defaultValue={this.state.data.salespersonUserId ? this.state.data.salespersonUserId.toString() : ""}>
                                            <option value="">Select a salesperson</option>
                                            {this.state.salespeople.map((value, index) => (
                                                <option value={value.id}>{value.fullName}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">Website</span>
                                        <input type="text" name="website" className="form-control" maxLength={50} defaultValue={this.state.data.website} />
                                    </div>
                                </div>

                                <h5 className="section-seprate">Contact Information</h5>
                                {this.makeContactList()}
                                <div className="field-group">
                                    <a href="#" onClick={this.handleAddContactClicked}>Add Contact Person</a>
                                </div>
                                <div className="form-actions">
                                    <div className="text-center">
                                        <button type="submit" className="btn btn-primary">Save</button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>;
    }

    public async componentDidMount() {
        if (this.state.isLoading) {
            let response = await apifetch("customers/add", undefined, true);
            if (response.ok) {
                let data = await response.json() as {
                    salespeople: UserIdAndName[],
                };

                this.setState({
                    salespeople: data.salespeople,
                });

                response = await apifetch("customers/" + this.props.match.params.id, undefined, true);
                if (response.ok) {
                    let data = await response.json() as Customer;
                    this.setState({
                        isLoading: false,
                        data: data,
                        contactPersonCount: data.customerContacts.length,
                    });

                    document.title = `Editing ${this.state.data.name} - Customers - ${SITE_NAME}`;
                }
            }
        }
    }

    private makeContactList() {
        let num = 0;
        return (
            this.state.data.customerContacts.map((contact, num) => (
                <div>
                    <ContactModel key={num} contactNumber={num} contactData={contact} onItemRemoved={this.handleRemoveContactClicked}
                        onHonorificSelected={this.handleOnHonorificSelected} onNameChanged={this.handleOnNameChanged} onPhoneChanged={this.handleOnPhoneChanged}
                        onTitleChanged={this.handleOnTitleChanged} onEmailChanged={this.handleOnEmailChanged} />

                </div>
            ))
        );
    }

    private async handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (this.state.hasValidationErrors) {
            this.setState({
                hasValidationErrors: false,
                errorMessage: undefined,
            });
        }

        let data = serialize(event.currentTarget, { hash: true }) as any;

        // Normalize phone numbers (aaa) bbb-cccc to aaabbbcccc
        if (data.phone) {
            data.phone = normalizePhone(data.phone);
        }
        if (data.fax) {
            data.fax = normalizePhone(data.fax);
        }

        data.customerContacts = this.state.data.customerContacts;

        if (data.customerContacts) {
            for (let i = 0; i < data.customerContacts.length; i++) {
                if (data.customerContacts[i].phone && data.customerContacts[i].phone.indexOf('(') >= 0) {
                    var normalizedPhone = normalizePhone(data.customerContacts[i].phone);
                    data.customerContacts[i].phone = normalizedPhone === null ? 0 : normalizedPhone;
                }
            }
        }

        let response = await apifetch("customers/" + this.props.match.params.id, {
            headers: new Headers({
                Accept: "application/json",
                "Content-Type": "application/json",
            }),
            method: "PUT",
            body: JSON.stringify(data),
        }, true);
        if (response.ok) {
            this.props.history.push("/customers/" + this.props.match.params.id);
        } else {
            const errorText = await extractErrorMessage(response);
            this.setState({
                hasValidationErrors: true,
                errorMessage: errorText,
            });
        }
    }

    private handleAddContactClicked(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();

        let newData = this.state.data;
        const newcontact: CustomerContact = {
            id: 0,
            honorificId: 0,
            honorificName: "",
            name: "",
            phone: "",
            title: "",
            email: ""
        }
        newData.customerContacts.push(newcontact);

        this.setState({
            data: newData,
            contactPersonCount: this.state.contactPersonCount + 1
        });
    }

    private handleRemoveContactClicked(contactNumber: number) {
        const newData = Object.assign([], this.state.data);
        newData.customerContacts.splice(contactNumber, 1);

        this.setState({
            data: newData
        });
    }

    private handleOnHonorificSelected(contactNumber: number, honorificId: number, honorificName: string | undefined) {

        const newData = Object.assign([], this.state.data);
        newData.customerContacts[contactNumber].honorificId = honorificId;

        if (honorificName !== undefined) {
            newData.customerContacts[contactNumber].honorificName = honorificName;
        }

        this.setState({
            data: newData
        });
    }

    private handleOnNameChanged(contactNumber: number, name: string | undefined) {
        const newData = Object.assign([], this.state.data);
        newData.customerContacts[contactNumber].name = name;

        this.setState({
            data: newData
        });
    }

    private handleOnPhoneChanged(contactNumber: number, phone: string | undefined) {
        const newData = Object.assign([], this.state.data);
        newData.customerContacts[contactNumber].phone = phone;

        this.setState({
            data: newData
        });
    }

    private handleOnTitleChanged(contactNumber: number, title: string | undefined) {
        const newData = Object.assign([], this.state.data);
        newData.customerContacts[contactNumber].title = title;

        this.setState({
            data: newData
        });
    }

    private handleOnEmailChanged(contactNumber: number, email: string | undefined) {
        const newData = Object.assign([], this.state.data);
        newData.customerContacts[contactNumber].email = email;

        this.setState({
            data: newData
        });
    }
}








// WEBPACK FOOTER //
// ./ClientApp/components/customers/Edit.tsx